import { AppState } from '../../types/store';
import { createSelector } from 'reselect';
import {
  Layout,
  SectionLayout,
  SectionLayoutZones,
  SectionLayoutZone,
} from '../../types/layout';
import { ModuleType } from '../../types/modules';
import { getAllModuleTypeByModuleId, getModules } from './modules';
import { isCurrentUserProfileOwner } from '../selectors/users';
import { getCurrentUserHasCore } from '@wix/da-shared-react/pkg/publicSession/selectors';

const reduxKey = 'layout';
function getSlice(state: any): Layout {
  if (!state[reduxKey]) {
    return {} as Layout;
  }
  return state[reduxKey];
}

export const getSectionLayout: (
  state: AppState,
  section: string
) => SectionLayout = createSelector(
  getSlice,
  (state: AppState, section: string) => section,
  (slice, section) => slice[section] || { version: 0, zones: {} }
);

export const getSectionLayoutVersion: (
  state: AppState,
  section: string
) => number = createSelector(getSectionLayout, layout => layout?.version || 0);

export const getSectionLayoutZones: (
  state: AppState,
  section: string
) => SectionLayoutZones = createSelector(
  getSectionLayout,
  layout => layout?.zones || {}
);

export const getSectionLayoutZone: (
  state: AppState,
  section: string,
  zone: string
) => SectionLayoutZone = createSelector(
  getSectionLayoutZones,
  (state: AppState, section: string, zone: string) => zone,
  (zones, zone) =>
    zones[zone] || { installableModuleTypes: [], installedModulesIds: [] }
);

export const getInstalledModuleIds: (
  state: AppState,
  section: string,
  zone: string
) => number[] = createSelector(
  getSectionLayoutZone,
  zone => zone?.installedModulesIds || []
);

export const getAllInstalledModuleIds: (
  state: AppState,
  section: string
) => number[] = createSelector(getSectionLayoutZones, zones =>
  Object.keys(zones).reduce(
    (acc, zone) => acc.concat(zones[zone]?.installedModulesIds || []),
    [] as number[]
  )
);

export const getInstalledModuleTypes: (
  state: AppState,
  section: string
) => ModuleType[] = createSelector(
  getAllInstalledModuleIds,
  getAllModuleTypeByModuleId,
  (moduleIds, moduleTypeByModuleId) =>
    moduleIds.map(id => moduleTypeByModuleId[id])
);

export const getInstallableModules = createSelector(
  [getSectionLayoutZone, getInstalledModuleTypes],
  (zoneLayout, installedModuleTypes) => {
    if (!zoneLayout) {
      return [];
    }

    const installedModuleCounts: Record<string, number> = {};
    installedModuleTypes.forEach(moduleType => {
      installedModuleCounts[moduleType] =
        installedModuleCounts[moduleType] || 0;
      installedModuleCounts[moduleType]++;
    });

    return (zoneLayout.installableModuleTypes || []).map(moduleInfo => ({
      ...moduleInfo,
      installedCount: installedModuleCounts[moduleInfo.moduleName] ?? 0,
    }));
  }
);

export const getInstallableModulesInfo = createSelector(
  [getInstallableModules, getCurrentUserHasCore],
  (installableModules, hasCore) =>
    installableModules.filter(({ installedCount, limit, restrict }) => {
      const coreLimit =
        restrict && restrict.priv === 'core' ? restrict.limit : 0;
      return installedCount < (hasCore ? Math.max(limit, coreLimit) : limit);
    })
);

export const getInstallableModuleTypes = createSelector(
  [getInstallableModulesInfo],
  installableModulesInfo =>
    installableModulesInfo.map(moduleInfo => moduleInfo.moduleName)
);

export const getCanCurrentUserEditModule = createSelector(
  [
    getModules,
    getInstallableModules,
    isCurrentUserProfileOwner,
    getCurrentUserHasCore,
    (_state, _section, _zone, moduleId) => moduleId,
  ],
  (modules, installableModules, isOwner, hasCore, moduleId) => {
    if (!isOwner || !modules || !modules[moduleId]) {
      return false;
    }
    const { type: moduleName } = modules[moduleId];
    const installableModule = installableModules.find(
      module => moduleName === module.moduleName
    );
    if (!installableModule || !installableModule.restrict) {
      return true;
    }
    // trying to find the position of the given moduleId in an array of
    // modulesIds of the same type, sorted by installation order
    const installedModules = Object.keys(modules)
      .filter(id => modules[id].type === moduleName)
      .map(id => parseInt(id))
      .sort();
    const installedIndex = installedModules.findIndex(id => id === moduleId);
    return (
      installedIndex <
      Math.max(
        installableModule.limit,
        hasCore ? installableModule.restrict.limit : 0
      )
    );
  }
);

export const getIsInstallingModule = createSelector(
  getSectionLayoutZone,
  zone => zone.isInstallingModule || false
);

export const getCurrentlyInstalledModulePostion = (
  state: AppState,
  section,
  zone
) => {
  const zoneLayout: SectionLayoutZone = getSectionLayoutZone(
    state,
    section,
    zone
  );
  return zoneLayout.currentlyInstalledModulePostion;
};
